"use client";

import { FilterIcon } from "@/components/Icons";
import { Button } from "@/components/ui/Button";
import { H1 } from "@/components/ui/Heading";
import { Separator } from "@/components/ui/Separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { useSearch } from "@/contexts/SearchContext";
import { PropertyListingType } from "@/enums/property.enum";
import { useSearchParams } from "@/hooks/useSearchParams";
import { logger } from "@/lib/logger";
import { convertSearchParamsToObject } from "@/lib/utils";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React from "react";
import { useToggle } from "usehooks-ts";
import { PropertySearchInput } from "./Search";

import PropertyFilter from "@/components/Property/PropertyListPage/FilterDialog";

const DesktopHeroText = () => {
  const t = useTranslations("home");
  const title = t("title").split(",");

  return (
    <div key="hero" className="flex justify-center items-center">
      <H1 className="font-normal text-center tracking-wide flex">
        {title.map((part, i, arr) => (
          <React.Fragment key={`part-${i}`}>
            <span>{part}</span>
            {i < arr.length - 1 && (
              <Separator
                key={`${i}-separator`}
                className="bg-primary w-[2px] md:w-1 mx-6 h-6 md:h-10"
                orientation="vertical"
              />
            )}
          </React.Fragment>
        ))}
      </H1>
    </div>
  );
};

export const HeroSection = () => {
  const t = useTranslations("shared");
  const [isFilterModelOpen, toggleFilterModel] = useToggle();

  const { handleSearch, listing, updateListing, locations, updateLocations } =
    useSearch();
  const searchParams = useSearchParams();

  if (searchParams.has("utm_term")) {
    logger.debug(
      {
        searchParams: convertSearchParamsToObject(searchParams),
      },
      "logging search params"
    );
  }

  return (
    <section className="mb-8 lg:m-[80px] relative">
      <Image
        src={"39f91d72-68fb-41db-52e8-57c2f17c2500"}
        className="lg:rounded-lg"
        alt="hero-image"
        loading="eager"
        objectFit="cover"
        priority={true}
        fetchPriority="high"
        fill
        sizes="(max-width: 768px) 30vw, 90vw"
      />
      <div className="flex flex-col py-10 px-6 md:py-[120px] gap-6 md:gap-[48px] justify-center items-center relative">
        <DesktopHeroText />
        <div className="bg-gray-50 flex flex-col gap-4 p-4 w-full md:w-2/3 rounded-md">
          <Tabs
            value={listing.type}
            className="!bg-gray-50 w-full overflow-x-auto scrollbar-thin"
            variant="underline"
            onValueChange={(value) =>
              updateListing({
                ...listing,
                type: value as PropertyListingType,
              })
            }
          >
            <div className="lg:w-full">
              <TabsList
                className="lg:w-full lg:grid lg:grid-cols-[15%_15%_25%_25%_20%]"
                size={"lg"}
              >
                <TabsTrigger
                  className="pb-3 lg:pb-4 px-4 lg:w-full"
                  value={PropertyListingType.BUY}
                >
                  {t("listing-type.buy")}
                </TabsTrigger>
                <TabsTrigger
                  className="pb-3 lg:pb-4 px-4 lg:w-full"
                  value={PropertyListingType.RENT}
                >
                  {t("listing-type.rent")}
                </TabsTrigger>
                <TabsTrigger
                  className="pb-3 lg:pb-4 px-4 lg:w-full"
                  value={PropertyListingType.COMMERCIAL_BUY}
                >
                  {t("listing-type.commercial-buy")}
                </TabsTrigger>
                <TabsTrigger
                  className="pb-3 lg:pb-4 px-4 lg:w-full"
                  value={PropertyListingType.COMMERCIAL_RENT}
                >
                  {t("listing-type.commercial-rent")}
                </TabsTrigger>
                <TabsTrigger
                  className="pb-3 lg:pb-4 px-4 lg:w-full"
                  value={PropertyListingType.OFF_PLAN}
                >
                  {t("listing-type.off-plan")}
                </TabsTrigger>
              </TabsList>
            </div>
          </Tabs>

          {isFilterModelOpen && (
            <PropertyFilter
              close={toggleFilterModel}
              isOpen={isFilterModelOpen}
            />
          )}

          <div className="flex flex-col lg:flex-row gap-2 p-0.5 max-w-full min-h-12 h-full">
            <div className="h-12 w-full">
              <PropertySearchInput
                variant="large"
                onChange={updateLocations}
                value={locations}
              />
            </div>

            <Button
              variant={"outline"}
              onClick={toggleFilterModel}
              className="min-w-24 w-full flex-1"
              size={"lg"}
            >
              <FilterIcon />
              {t("search.filter")}
            </Button>

            <Button
              onClick={() => handleSearch(true)}
              size={"lg"}
              className="w-full min-w-24 flex-1"
            >
              {t("search.search")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
